/** Mês/Ano do período, sem dia nem hora. */
export interface PeriodMonth {
  /** Ano do período (ex.: 2026). */
  year: number;
  /** Mês do período, de 1 (janeiro) a 12 (dezembro). */
  month: number;
}

/** Intervalo de Mês/Ano: início e fim, ambos inclusivos. */
export interface PeriodRange {
  start: PeriodMonth;
  end: PeriodMonth;
}

/** Limite de caracteres da busca, conforme o contrato. */
export const BUSCA_MAX_LENGTH = 255;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Data local no formato `YYYY-MM-DD` do contrato (sem passar por UTC). */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Período padrão: o ano corrente, de janeiro a dezembro. */
export function defaultPeriodo(today: Date = new Date()): PeriodRange {
  const year = today.getFullYear();
  return { start: { year, month: 1 }, end: { year, month: 12 } };
}

/** Intervalo no formato do `MonthRangePicker` do DS (`from`/`to`), ambos inclusivos. */
export interface MonthPeriodRange {
  from: PeriodMonth;
  to: PeriodMonth;
}

/** O mês corrente como intervalo de um mês só. */
export function currentMonthRange(today: Date = new Date()): MonthPeriodRange {
  const month = { year: today.getFullYear(), month: today.getMonth() + 1 };
  return { from: month, to: { ...month } };
}

/** Intervalo do `MonthRangePicker` do DS no `YYYY-MM` do contrato. */
export function monthRangeToValues({ from, to }: MonthPeriodRange): { from: string; to: string } {
  return periodoToMonthValues({ start: from, end: to });
}

/** Período no formato `YYYY-MM` do contrato (o mesmo da Carga de Profissionais). */
export function periodoToMonthValues(periodo: PeriodRange): { from: string; to: string } {
  const format = ({ year, month }: PeriodMonth) => `${year}-${pad(month)}`;
  return { from: format(periodo.start), to: format(periodo.end) };
}

/** Inverso de `periodoToMonthValues`: lê o `YYYY-MM` devolvido pelo `MonthRangePicker`. */
export function monthValuesToPeriodo(range: { from: string; to: string }): PeriodRange {
  const parse = (value: string): PeriodMonth => {
    const [year = 0, month = 0] = value.split("-").map(Number);
    return { year, month };
  };
  return { start: parse(range.from), end: parse(range.to) };
}

/** `dataInicio` = 1º dia do mês inicial; `dataFim` = último dia do mês final. */
export function periodoToRange(periodo: PeriodRange): { dataInicio: string; dataFim: string } {
  const { start, end } = periodo;
  return {
    dataInicio: toIsoDate(new Date(start.year, start.month - 1, 1)),
    dataFim: toIsoDate(new Date(end.year, end.month, 0)),
  };
}
